import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { messagesApi, authApi } from '../lib/api';
import { encryptMessage, decryptMessage, getPrivateKey, hasLocalKey, isClientEncrypted } from '../lib/crypto';
import TranslatableMessage from '../components/TranslatableMessage';
import {
  MessageCircle, Send, ArrowLeft, User, Loader2, Shield, Lock, Search, ImagePlus, X
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

export default function MessagesPage({ recipientId: propRecipientId, recipientName: propRecipientName, onBack }) {
  const { t, language } = useLanguage();
  const { user, isAuthenticated } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const msgFileRef = useRef(null);
  const [messageMedia, setMessageMedia] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Check for recipient from sessionStorage (from clicking author name)
  const [recipientId, setRecipientId] = useState(propRecipientId);
  const [recipientName, setRecipientName] = useState(propRecipientName);

  useEffect(() => {
    const storedRecipient = sessionStorage.getItem('sg_message_recipient');
    if (storedRecipient && !propRecipientId) {
      try {
        const { id, name } = JSON.parse(storedRecipient);
        setRecipientId(id);
        setRecipientName(name);
        sessionStorage.removeItem('sg_message_recipient');
      } catch (e) {
        console.error('Failed to parse recipient:', e);
      }
    }
  }, [propRecipientId]);

  // Load conversations or specific conversation
  useEffect(() => {
    if (!isAuthenticated) return;
    
    if (recipientId) {
      // Direct message to specific user
      loadConversation(recipientId);
    } else {
      loadConversations();
    }
  }, [isAuthenticated, recipientId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    setLoading(true);
    try {
      const res = await messagesApi.getConversations();
      setConversations(res.data.conversations || []);
    } catch (err) {
      toast.error(t('failed_load_conversations') || 'Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const loadConversation = async (partnerId) => {
    setLoading(true);
    try {
      const res = await messagesApi.getConversation(partnerId);
      const rawMessages = res.data.messages || [];
      
      // ZERO-KNOWLEDGE: Try to decrypt v3 messages client-side
      const userId = user?.username || user?.id;
      const privKey = userId ? await getPrivateKey(userId) : null;
      
      const decryptedMessages = rawMessages.map(msg => {
        if (msg.e2e_content && privKey && isClientEncrypted(msg.e2e_content)) {
          const decrypted = decryptMessage(msg.e2e_content, privKey);
          if (decrypted) return { ...msg, content: decrypted, _decrypted: true };
        }
        return msg;  // Fallback: use server-stored content (legacy v1/v2)
      });
      
      setMessages(decryptedMessages);
      setSelectedConversation({
        partner_id: partnerId,
        partner_name: res.data.partner?.pseudonym || recipientName || 'Unknown',
        partner_photo: res.data.partner?.profile_photo,
      });
    } catch (err) {
      toast.error(t('failed_load_messages') || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSearch = async (q) => {
    setSearchQuery(q);
    if (q.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await authApi.searchUsers(q);
      setSearchResults(res.data.users || []);
    } catch { setSearchResults([]); }
    finally { setSearching(false); }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !messageMedia) return;
    if (!selectedConversation) return;
    
    setSending(true);
    try {
      const plaintext = newMessage.trim() || '📷';
      let e2ePayload = null;
      const mediaPayload = messageMedia ? [{ type: 'image', data: messageMedia, caption: '' }] : null;
      
      const partnerProfile = await authApi.getUserProfile(selectedConversation.partner_id);
      const recipientPubKey = partnerProfile.data?.public_key;
      if (recipientPubKey) {
        try {
          e2ePayload = encryptMessage(plaintext, recipientPubKey);
        } catch (e) {}
      }
      
      const res = await messagesApi.send(selectedConversation.partner_id, plaintext, language, e2ePayload, mediaPayload);
      setMessages([...messages, res.data]);
      setNewMessage('');
      setMessageMedia(null);
      toast.success(t('message_sent'));
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Failed to send message';
      if (errorMsg.includes('disabled')) {
        toast.error(t('messages_disabled'));
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSending(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (selectedConversation && !recipientId) {
      setSelectedConversation(null);
      setMessages([]);
      loadConversations();
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="px-4 pt-4 pb-4">
        <h1 className="text-xl font-[var(--font-heading)] tracking-[-0.01em] text-foreground flex items-center gap-2 mb-1">
          <MessageCircle className="w-5 h-5 text-primary" />
          {t('messages')}
        </h1>
        <div className="flex items-center gap-1.5 mb-5">
          <Shield className="w-3 h-3 text-green-500" />
          <span className="text-[10px] text-green-500 font-medium">{t('encrypted') || 'End-to-end encrypted'}</span>
        </div>
        <div className="text-center py-12 text-muted-foreground">
          <Shield className="w-12 h-12 mx-auto mb-3 text-green-500/30" />
          <p>{t('login_to_message') || 'Please log in to view messages'}</p>
          <p className="text-[10px] mt-2 text-green-500/70">{t('messages_encrypted') || 'Messages are end-to-end encrypted'}</p>
        </div>
      </div>
    );
  }

  // Conversation view
  if (selectedConversation) {
    return (
      <div className="flex flex-col h-[calc(100vh-120px)]">
        {/* Header */}
        <div className="px-4 py-3 border-b border-border flex items-center gap-3">
          <button onClick={handleBack} className="p-1 hover:bg-accent rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
            {selectedConversation.partner_photo ? (
              <img 
                src={selectedConversation.partner_photo} 
                alt="" 
                className="w-full h-full object-cover"
              />
            ) : (
              <span className="text-sm font-medium text-primary">
                {(selectedConversation.partner_name || 'A').charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <span className="font-medium">{selectedConversation.partner_name}</span>
          <div className="ml-auto flex items-center gap-1">
            <Lock className="w-3 h-3 text-green-500" />
            <span className="text-[9px] text-green-500 font-medium">E2E</span>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              {t('no_messages')}
            </div>
          ) : (
            messages.map((msg) => {
              const isMine = msg.sender_id === user?.id;
              return (
                <div
                  key={msg.id}
                  className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                >
                  <TranslatableMessage
                    message={msg}
                    isOwnMessage={isMine}
                  />
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="px-4 py-3 border-t border-border">
          <div className="flex items-center justify-center gap-1 mb-1.5">
            <Lock className="w-2.5 h-2.5 text-green-500/60" />
            <span className="text-[9px] text-green-500/60">{t('messages_encrypted') || 'Messages are end-to-end encrypted'}</span>
          </div>
          {/* Media preview */}
          {messageMedia && (
            <div className="mb-2 relative inline-block">
              <img src={messageMedia} alt="" className="h-20 rounded-lg border border-border" />
              <button onClick={() => setMessageMedia(null)}
                className="absolute -top-1 -right-1 p-0.5 bg-destructive rounded-full">
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <input ref={msgFileRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f && f.size < 3000000) {
                  const r = new FileReader();
                  r.onload = () => setMessageMedia(r.result);
                  r.readAsDataURL(f);
                } else if (f) toast.error(t('image_too_large') || 'Image too large');
                e.target.value = '';
              }} />
            <button onClick={() => msgFileRef.current?.click()}
              className="p-2.5 rounded-xl border border-border text-muted-foreground hover:bg-accent transition-colors"
              data-testid="message-add-photo">
              <ImagePlus className="w-4 h-4" />
            </button>
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
              placeholder={t('type_message')}
              className="flex-1 px-4 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              data-testid="message-input"
            />
            <button
              onClick={handleSendMessage}
              disabled={sending || !newMessage.trim()}
              className="p-2.5 rounded-xl bg-primary text-primary-foreground disabled:opacity-50"
              data-testid="message-send"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Conversations list
  return (
    <div className="px-4 pt-4 pb-4">
      <h1 className="text-xl font-[var(--font-heading)] tracking-[-0.01em] text-foreground flex items-center gap-2 mb-1">
        <MessageCircle className="w-5 h-5 text-primary" />
        {t('messages')}
      </h1>
      <div className="flex items-center gap-1.5 mb-5">
        <Shield className="w-3 h-3 text-green-500" />
        <span className="text-[10px] text-green-500 font-medium">{t('encrypted') || 'End-to-end encrypted'}</span>
      </div>

      {/* User Search */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleUserSearch(e.target.value)}
            placeholder={t('search_users') || 'Search users...'}
            className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            data-testid="user-search-input"
          />
        </div>
        {searchResults.length > 0 && (
          <div className="mt-2 border border-border rounded-xl overflow-hidden bg-card">
            {searchResults.map((u) => (
              <button
                key={u.id}
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  loadConversation(u.id);
                  setRecipientName(u.pseudonym || u.username);
                }}
                className="w-full flex items-center gap-3 p-3 hover:bg-accent/50 transition-colors border-b border-border/50 last:border-0"
                data-testid={`user-search-result-${u.id}`}
              >
                <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                  {u.profile_photo ? (
                    <img src={u.profile_photo} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs font-medium text-primary">{(u.pseudonym || u.username || 'A').charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium">{u.pseudonym || u.username}</p>
                  <p className="text-[10px] text-muted-foreground">@{u.username}</p>
                </div>
              </button>
            ))}
          </div>
        )}
        {searching && <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-primary" /></div>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>{t('no_messages')}</p>
          <p className="text-xs mt-1">{t('start_conversation_hint') || 'Click on a user\'s name to start a conversation'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {conversations.map((conv) => (
            <button
              key={conv.partner_id}
              onClick={() => loadConversation(conv.partner_id)}
              className="w-full flex items-center gap-3 p-3 rounded-xl border border-border bg-card hover:bg-accent/50 transition-colors"
              data-testid={`conversation-${conv.partner_id}`}
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                {conv.partner_photo ? (
                  <img src={conv.partner_photo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-sm font-medium text-primary">
                    {(conv.partner_name || 'A').charAt(0).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="flex-1 text-left">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{conv.partner_name}</span>
                  {conv.unread_count > 0 && (
                    <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                      {conv.unread_count}
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground truncate">
                  {conv.last_message?.content}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
